// @ts-check
// =============================================================================
// js/ui/modal.js — one generic modal overlay.
//
// Used by the login picker, the upsell prompts, and confirm dialogs. Exactly
// one modal can be open at a time (opening a new one closes the old — that
// matches how the old client's ad-hoc overlays behaved).
//
//   openModal({ title, body, buttons: [{label, className?, onClick}] })
//   closeModal()
//
// `body` may be an HTMLElement or an HTML string (trusted, app-authored only —
// NEVER pass server/user strings without escaping; use escapeHtml below).
// =============================================================================

/** Currently open modal element, if any. */
let openEl = /** @type {HTMLElement|null} */ (null);

// A modal belongs to the screen that opened it — navigating away closes it
// (otherwise an upsell could linger over a different screen, e.g. the duel).
window.addEventListener('hashchange', () => closeModal());

/**
 * Escape a string for safe interpolation into innerHTML.
 * @param {any} value
 * @returns {string}
 */
export function escapeHtml(value) {
	return String(value)
		.replaceAll('&', '&amp;')
		.replaceAll('<', '&lt;')
		.replaceAll('>', '&gt;')
		.replaceAll('"', '&quot;')
		.replaceAll("'", '&#39;');
}

/**
 * Open a modal. Clicking the dim backdrop closes it.
 * @param {{
 *   title?: string,
 *   body?: HTMLElement|string,
 *   buttons?: Array<{label: string, className?: string, onClick?: () => void}>,
 *   dismissible?: boolean,
 * }} options
 * @returns {HTMLElement}  The modal box (to append extra content if needed).
 */
export function openModal({ title, body, buttons = [], dismissible = true }) {
	closeModal();

	const overlay = document.createElement('div');
	overlay.className = 'modal-overlay';
	if (dismissible) {
		overlay.addEventListener('click', (e) => {
			if (e.target === overlay) closeModal();
		});
	}

	const box = document.createElement('div');
	box.className = 'modal-box';

	if (title) {
		const titleEl = document.createElement('div');
		titleEl.className = 'modal-title';
		titleEl.textContent = title;
		box.appendChild(titleEl);
	}

	if (body instanceof HTMLElement) {
		box.appendChild(body);
	} else if (typeof body === 'string') {
		const bodyEl = document.createElement('div');
		bodyEl.innerHTML = body;
		box.appendChild(bodyEl);
	}

	if (buttons.length) {
		// Actions stack vertically and fill the width, so every button is the
		// same size (no ragged inline row) and the primary CTA reads first.
		const actions = document.createElement('div');
		actions.className = 'modal-actions';
		for (const btn of buttons) {
			const buttonEl = document.createElement('button');
			buttonEl.textContent = btn.label;
			buttonEl.className = btn.className || 'defaultButton';
			buttonEl.addEventListener('click', () => {
				// Buttons close by default; the handler can reopen if it wants.
				closeModal();
				btn.onClick?.();
			});
			actions.appendChild(buttonEl);
		}
		box.appendChild(actions);
	}

	overlay.appendChild(box);
	document.body.appendChild(overlay);
	openEl = overlay;
	return box;
}

/**
 * Close the open modal (no-op when none is open).
 * @returns {void}
 */
export function closeModal() {
	openEl?.remove();
	openEl = null;
}
